'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/** Products shown in the listing: positions `from`–`to` (1-based) out of `total`. */
export type ListingRange = { from: number; to: number; total: number };

// Two contexts: the reporting product list subscribes only to the stable setter, so a new range
// re-renders the toolbar, not the grid.
const RangeContext = createContext<ListingRange | null>(null);
const SetRangeContext = createContext<((range: ListingRange | null) => void) | null>(null);

export function ListingRangeProvider({ children }: { children: ReactNode }) {
  const [range, setRange] = useState<ListingRange | null>(null);

  return (
    <SetRangeContext.Provider value={setRange}>
      <RangeContext.Provider value={range}>{children}</RangeContext.Provider>
    </SetRangeContext.Provider>
  );
}

/** The range reported by the product list, `null` until it has data. */
export function useListingRange() {
  return useContext(RangeContext);
}

/**
 * Reports the visible range to the listing toolbar: `undefined` (loading) keeps the last range, so
 * the count does not blink while another sort or filter loads; `null` clears it. No-op outside the
 * provider.
 */
export function useReportListingRange(range: ListingRange | null | undefined) {
  const setRange = useContext(SetRangeContext);
  const loading = range === undefined;
  const from = range?.from;
  const to = range?.to;
  const total = range?.total;

  useEffect(() => {
    if (!setRange || loading) return;
    setRange(
      from !== undefined && to !== undefined && total !== undefined ? { from, to, total } : null,
    );
  }, [setRange, loading, from, to, total]);

  useEffect(() => {
    if (!setRange) return;
    return () => setRange(null);
  }, [setRange]);
}
